import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from "motion/react";
import { memo, type ReactNode } from "react";
import * as css from "./intake-modal.module.css";

const STEP_TRANSITION_DISTANCE = 32;
const STEP_TRANSITION = { duration: 0.24, ease: "easeOut" } as const;

// Function variants so the exiting step reads the CURRENT direction (via
// AnimatePresence `custom`). Forward (1) slides left; backward (-1) slides right.
const stepVariants: Variants = {
  initial: (direction: number) => ({
    opacity: 0,
    x: direction === 1 ? STEP_TRANSITION_DISTANCE : -STEP_TRANSITION_DISTANCE,
  }),
  animate: { opacity: 1, x: 0 },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction === 1 ? -STEP_TRANSITION_DISTANCE : STEP_TRANSITION_DISTANCE,
  }),
};

export const IntakeStepTransition = memo(function IntakeStepTransition({
  children,
  direction = 1,
  enabled = true,
  stepKey,
}: {
  children: ReactNode;
  direction?: number;
  enabled?: boolean;
  stepKey: string;
}) {
  const shouldReduceMotion = useReducedMotion();

  if (!enabled) {
    return <>{children}</>;
  }

  if (shouldReduceMotion) {
    return <div className={css.stepTransitionFrame}>{children}</div>;
  }

  return (
    <AnimatePresence custom={direction} mode="wait">
      <motion.div
        animate="animate"
        className={css.stepTransitionFrame}
        custom={direction}
        exit="exit"
        initial="initial"
        key={stepKey}
        transition={STEP_TRANSITION}
        variants={stepVariants}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
});
